import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getBackupList, createBackupDump } from '../../services/backup.service';

export default function StepBackup({ onNext, onSalir, setAlertInfo }) {
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dumps, setDumps] = useState([]);
  const [hasBackedUp, setHasBackedUp] = useState(false);
  const [warningOpen, setWarningOpen] = useState(false);

  useEffect(() => {
    loadDumps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDumps = async () => {
    setLoading(true);
    try {
      const list = await getBackupList();
      setDumps(list);
    } catch (error) {
      setAlertInfo({
        open: true,
        message: `Error al cargar backups: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    setProgress(0);

    const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
    const eventSource = new EventSource(`${baseUrl}/api/backup/progress`);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (typeof data.percentage === 'number') {
          setProgress(data.percentage);
        }
      } catch (e) {
        // ignore parse errors
      }
    };

    eventSource.onerror = () => {
      eventSource.close();
    };

    try {
      await createBackupDump();
      setProgress(100);
      setAlertInfo({
        open: true,
        message: 'Backup generado correctamente.',
        severity: 'success',
      });
      setHasBackedUp(true);
      await loadDumps();
    } catch (error) {
      setAlertInfo({
        open: true,
        message: `Error al generar backup: ${error.message}`,
        severity: 'error',
      });
    } finally {
      eventSource.close();
      setCreating(false);
    }
  };

  const handleNextClick = () => {
    if (!hasBackedUp) {
      setWarningOpen(true);
    } else {
      onNext();
    }
  };

  const handleWarningConfirm = () => {
    setWarningOpen(false);
    onNext();
  };

  const handleWarningCancel = () => {
    setWarningOpen(false);
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" sx={{ color: '#e2e8f0', mb: 1, fontWeight: 'bold' }}>
        Resguardo de Base de Datos
      </Typography>
      <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
        Antes de proceder con la generación o cálculo de los datos, te recomendamos hacer un volcado (dump) completo de la base de datos por seguridad.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4, width: '100%' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={!creating && <SaveIcon />}
          onClick={handleCreateBackup}
          disabled={creating}
          sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: '#fff',
            px: 4,
            py: 1.5,
            fontWeight: 'bold',
            borderRadius: 1,
            mb: creating ? 2 : 0,
            '&:hover': {
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
            },
          }}
        >
          {creating ? 'Generando Backup...' : 'Generar Backup Ahora'}
        </Button>
        {creating && (
          <Box sx={{ width: '100%', maxWidth: 400, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: '100%' }}>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
              <Typography variant="body2" color="#cbd5e1">{`${Math.round(progress)}%`}</Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Typography variant="subtitle2" sx={{ color: '#cbd5e1', mb: 2, fontWeight: 600 }}>
        Backups Anteriores
      </Typography>
      
      <TableContainer component={Paper} sx={{ background: 'rgba(15, 23, 42, 0.5)', border: '1px solid rgba(255,255,255,0.1)', mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Archivo</TableCell>
              <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Fecha</TableCell>
              <TableCell align="right" sx={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Tamaño</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 3, borderBottom: 'none' }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : dumps.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 3, color: '#475569', borderBottom: 'none' }}>
                  No hay backups registrados.
                </TableCell>
              </TableRow>
            ) : (
              dumps.map((row) => (
                <TableRow key={row.filename} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell sx={{ color: '#cbd5e1', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    {row.filename}
                  </TableCell>
                  <TableCell sx={{ color: '#cbd5e1', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    {new Date(row.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#cbd5e1', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    {formatBytes(row.sizeBytes)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Acciones */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Button variant="outlined" onClick={onSalir} sx={{ color: '#cbd5e1', borderColor: 'rgba(255,255,255,0.2)' }}>
          Salir
        </Button>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={handleNextClick}
          sx={{ background: '#3a6eb6', color: '#fff', '&:hover': { background: '#2c5282' } }}
        >
          Siguiente
        </Button>
      </Box>

      {/* Warning Dialog */}
      <Dialog open={warningOpen} onClose={handleWarningCancel} PaperProps={{ sx: { background: '#1e293b', color: '#fff' } }}>
        <DialogTitle sx={{ color: '#f59e0b' }}>Advertencia de Seguridad</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#cbd5e1' }}>
            No has realizado un resguardo de la base de datos en esta sesión. Si ocurre algún error durante el cálculo del ranking, los datos podrían corromperse.
            <br /><br />
            ¿Estás seguro de que deseas continuar sin hacer un backup?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWarningCancel} sx={{ color: '#94a3b8' }}>
            Cancelar y Volver
          </Button>
          <Button onClick={handleWarningConfirm} sx={{ color: '#ef4444', fontWeight: 'bold' }}>
            Continuar sin Backup
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
